// Share button for /tool/:slug pages. Mirrors ShareCompareButton: opens a
// popover with a live OG preview, the canonical share URL, copy + native
// share. The OG image always points at the tool-og edge function.
import { useMemo, useState } from "react";
import { Check, Copy, Image as ImageIcon, Share2 } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";

interface Props {
  slug: string;
  name: string;
}

const ShareToolButton = ({ slug, name }: Props) => {
  const [copied, setCopied] = useState(false);
  const [imgError, setImgError] = useState(false);

  const shareUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/tool/${slug}`;
  }, [slug]);

  const ogImageUrl = useMemo(() => {
    const projectId =
      (import.meta.env.VITE_SUPABASE_PROJECT_ID as string) || "";
    if (!projectId) return "";
    return `https://${projectId}.supabase.co/functions/v1/tool-og?image=1&slug=${encodeURIComponent(slug)}`;
  }, [slug]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — long-press the link to copy manually");
    }
  };

  const handleNativeShare = async () => {
    if (typeof navigator === "undefined" || !navigator.share) {
      handleCopy();
      return;
    }
    try {
      await navigator.share({
        title: `${name} on HackerLab`,
        text: `${name} on HackerLab`,
        url: shareUrl,
      });
    } catch {
      // user cancelled
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Share tool"
          className="inline-flex items-center gap-1.5 text-xs font-mono px-3 py-2 rounded-lg border border-border bg-card text-foreground hover:bg-secondary"
        >
          <Share2 className="w-3.5 h-3.5" />
          Share
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[min(420px,calc(100vw-2rem))] p-3 font-mono"
      >
        <div className="text-[11px] text-muted-foreground uppercase tracking-wider mb-2">
          Preview
        </div>
        <div className="relative aspect-[1200/630] w-full rounded-md overflow-hidden border border-border bg-secondary/50 mb-3">
          {ogImageUrl && !imgError ? (
            <img
              src={ogImageUrl}
              alt="Share preview"
              className="w-full h-full object-cover"
              onError={() => setImgError(true)}
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[11px] text-muted-foreground gap-2">
              <ImageIcon className="w-4 h-4" />
              Preview unavailable
            </div>
          )}
        </div>

        <div className="text-[11px] text-muted-foreground uppercase tracking-wider mb-1">
          Link
        </div>
        <div className="flex items-stretch gap-1.5 mb-2">
          <input
            type="text"
            readOnly
            value={shareUrl}
            onFocus={(e) => e.currentTarget.select()}
            className="flex-1 min-w-0 text-[11px] px-2 py-1.5 rounded-md border border-border bg-background text-foreground"
          />
          <button
            type="button"
            onClick={handleCopy}
            aria-label="Copy link"
            className="inline-flex items-center justify-center px-2 rounded-md border border-border bg-card hover:bg-secondary"
          >
            {copied ? (
              <Check className="w-3.5 h-3.5 text-primary" />
            ) : (
              <Copy className="w-3.5 h-3.5 text-muted-foreground" />
            )}
          </button>
        </div>

        <button
          type="button"
          onClick={handleNativeShare}
          className="w-full inline-flex items-center justify-center gap-1.5 text-xs px-3 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Share2 className="w-3.5 h-3.5" />
          Share…
        </button>

        <p className="text-[10px] text-muted-foreground mt-2 leading-relaxed">
          Image preview shows what links unfurl to on Slack, X, LinkedIn, etc.
        </p>
      </PopoverContent>
    </Popover>
  );
};

export default ShareToolButton;
